import { Outlet, useLocation } from 'react-router-dom';
import type { ErrorInfo } from 'react';
import { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import AppHeader from '../components/common/AppHeader';
import { SubHeaderStages } from '../components/common/SubHeaderStages';
import { FloatingActionBar } from '../components/common/FloatingActionBar';
import { InlineToolbarSlot } from '../components/common/InlineToolbarSlot';
import { ErrorBoundary } from '../components/common/ErrorBoundary';
import { PageTransition } from '../components/layouts/PageTransition';
import { ImpersonationBanner } from '../features/impersonation/ImpersonationBanner';

import { useErrorHandler } from '../hooks/useErrorHandler';
import { useRbac } from '../contexts/RbacContext';
import { ToolbarProvider } from '../contexts/ToolbarContext';
import {
  MAIN_NAVIGATION,
  getActiveNavKey,
} from '../config/navigation.config';
import { useFeatureFlagsPublic } from '../hooks/useFeatureFlags';
import { useUIConfig } from '../contexts/UIConfigContext';
import { resolveIcon } from '../utils/iconResolver';
import styles from '../styles/layouts/AppLayout.module.scss';

const NAV_ICON_STYLE = { fontSize: 16 } as const;

/** Кэш React elements для иконок навигации — стабильные ссылки между рендерами */
const iconCache = new Map<string, React.ReactElement>();
function getCachedIcon(name: string): React.ReactElement {
  let el = iconCache.get(name);
  if (!el) {
    el = resolveIcon(name, NAV_ICON_STYLE);
    iconCache.set(name, el);
  }
  return el;
}

export const MainLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { notifyError } = useErrorHandler();
  const { hasAnyChildPermission, isLoading: rbacLoading } = useRbac();

  const handleError = (error: Error, errorInfo: ErrorInfo) => {
    notifyError(error, {
      showNotification: false,
      logError: true,
      customMessage: `Ошибка на странице: ${location.pathname}`
    });

    if (import.meta.env.PROD) {
      console.error('Sending error to analytics:', {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        url: location.pathname,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      });
    }
  };

  const activeItem = useMemo(
    () => getActiveNavKey(location.pathname),
    [location.pathname]
  );

  const handleNavigate = useCallback(
    (key: string) => {
      const item = MAIN_NAVIGATION.find((nav) => nav.key === key);
      navigate(item?.path ?? '/');
    },
    [navigate]
  );

  const { data: featureFlags } = useFeatureFlagsPublic();
  const { getNavLabel, getNavIcon } = useUIConfig();

  // Вычисляем строковые имена иконок (стабильные, не создают React elements)
  const navIconNames = useMemo(() => {
    const map: Record<string, string> = {};
    for (const item of MAIN_NAVIGATION) {
      map[item.key] = getNavIcon(item.key, item.defaultIconName);
    }
    return map;
  }, [getNavIcon]);

  const navigationItems = useMemo(() => {
    if (rbacLoading) {
      return MAIN_NAVIGATION
        .filter((item) => !item.featureFlag)
        .map(({ key, label }) => ({
          key,
          label: getNavLabel(key, label),
          icon: getCachedIcon(navIconNames[key]),
          loading: true,
        }));
    }

    return MAIN_NAVIGATION
      .filter((item) => {
        if (item.featureFlag) {
          const flag = featureFlags?.find((f) => f.key === item.featureFlag);
          if (!flag?.enabled) return false;
        }
        if (item.rbac) {
          return hasAnyChildPermission(item.rbac.resource, item.rbac.permission);
        }
        return true;
      })
      .map(({ key, label }) => ({
        key,
        label: getNavLabel(key, label),
        icon: resolveIcon(navIconNames[key], NAV_ICON_STYLE),
      }));
  }, [hasAnyChildPermission, rbacLoading, featureFlags, getNavLabel, navIconNames]);

  // Sub-header виден на страницах этапов (/sales/stage-N, /purchase/stage-N)
  // и на страницах модулей справочников (/references/:moduleId)
  const isStagesPage = useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    const module = segments[0];
    const stage = segments[1];
    if ((module === 'sales' || module === 'purchase') && /^stage-\d+$/.test(stage ?? '')) {
      return true;
    }
    if (module === 'reconciliation' && !!stage) {
      return true;
    }
    if (module === 'references' && !!stage) {
      return true;
    }
    if (module === 'methodology' && !!stage) {
      return true;
    }
    return false;
  }, [location.pathname]);

  return (
    <ToolbarProvider>
      <div className={styles.appLayout}>
        <ImpersonationBanner />
        <AppHeader
          activeItem={activeItem}
          onNavigate={handleNavigate}
          navigationItems={navigationItems}
          loading={rbacLoading}
        />

        <AnimatePresence>
          {isStagesPage && <SubHeaderStages key="sub-header-stages" />}
        </AnimatePresence>

        <main className={styles.fixedContent}>
          <div className={styles.contentWrapper}>
            <div className={styles.contentContainer}>
              {/* Compact inline toolbar for non-stage pages (References, etc.) */}
              {!isStagesPage && <InlineToolbarSlot />}
              <ErrorBoundary
                onError={handleError}
                showDetails={import.meta.env.DEV}
              >
                <PageTransition>
                  <Outlet />
                </PageTransition>
              </ErrorBoundary>
            </div>
          </div>
        </main>

        {/* Floating Save bar — appears when there are unsaved changes */}
        <FloatingActionBar />
      </div>
    </ToolbarProvider>
  );
};

export default MainLayout;
